import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { ComponentProps, Ref } from "react";
import {
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";

type SearchInputProps = {
  placeholder: string;
  onChangeText: (text: string) => void;
  onPendingChange?: (pending: boolean) => void;
  icon?: ComponentProps<typeof MaterialCommunityIcons>["name"];
  ref?: Ref<SearchInputHandle>;
};

export type SearchInputHandle = {
  clear: () => void;
};

export const SearchInput = ({
  placeholder,
  onChangeText,
  onPendingChange,
  icon,
  ref,
}: SearchInputProps) => {
  const [inputText, setInputText] = useState("");
  const [pending, setPending] = useState(false);
  const [isFocused, setIsFocued] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTextChange = useCallback(
    (text: string) => {
      setInputText(text);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Will not debounce on empty input value
      if (text === "") {
        onChangeText("");
        setPending(false);
        return;
      }

      setPending(true);

      debounceTimeoutRef.current = setTimeout(() => {
        onChangeText(text);
        setPending(false);
      }, 300);
    },
    [onChangeText],
  );

  useEffect(() => {
    if (onPendingChange) {
      onPendingChange(pending);
    }
  }, [pending, onPendingChange]);

  const clearValue = useCallback(() => {
    handleTextChange("");
  }, [handleTextChange]);

  useImperativeHandle(
    ref,
    () => ({
      clear: clearValue,
    }),
    [clearValue],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const { colors } = useCustomTheme();

  return (
    <View style={styles.formGroup}>
      <View style={styles.inputContainer}>
        {icon && (
          <MaterialCommunityIcons name={icon} size={24} color={colors.text} />
        )}
        <View style={styles.inputGroup}>
          {placeholder && (isFocused || (inputText && inputText.length > 0)) ? (
            <Text style={styles.label}>{placeholder}</Text>
          ) : null}
          <TextInput
            style={styles.input}
            value={inputText}
            onChangeText={handleTextChange}
            placeholder={!isFocused ? placeholder : undefined}
            onFocus={() => setIsFocued(true)}
            onBlur={() => setIsFocued(false)}
          />
        </View>

        <TouchableOpacity
          testID="clear-button"
          style={[
            styles.searchIcon,
            {
              backgroundColor:
                colors[inputText && inputText.length > 0 ? "text" : "border"],
            },
          ]}
          onPress={clearValue}
          disabled={inputText.length === 0}
        >
          <MaterialCommunityIcons
            name={inputText && inputText.length > 0 ? "close" : "magnify"}
            size={24}
            color={colors.background}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 0,
  },
  label: {
    fontSize: 14,
    marginLeft: 3,
  },
  input: {
    height: 45,
    width: "100%",
    fontSize: 20,
    lineHeight: 28,
    ...Platform.select({
      web: {
        outlineStyle: "none",
      } as any,
    }),
  },
  inputGroup: {
    height: "100%",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  inputContainer: {
    height: 80,
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    gap: 12,
    borderRadius: 40,
    borderWidth: 2,
    borderColor: "#b2b2b2",
    paddingHorizontal: 24,
    paddingVertical: 20,
    backgroundColor: "white",
  },
  searchIcon: {
    marginRight: -16,
    padding: 10,
    height: 64,
    width: 64,
    borderRadius: 32,
    justifyContent: "center",
    alignItems: "center",
  },
});
